import { useEffect, useRef, useState } from "react";
import { useRTC, formatRtcDuration } from "@/context/RTCContext";
import { useAuth } from "@/context/AuthContext";
import {
  MicrophoneSlash, Microphone, PhoneDisconnect, Users,
  ArrowsIn, PauseCircle, PlayCircle, CheckCircle, ArrowsClockwise, WifiSlash,
} from "@phosphor-icons/react";

/* ============================================================
 *  Speaking analyser + <audio> hooks
 * ============================================================ */
function useSpeakingAnalyser(stream) {
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => {
    if (!stream) return undefined;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return undefined;
    // iter144: guard against empty-stream `InvalidStateError` seen in
    // production Sentry. `createMediaStreamSource` throws synchronously
    // when the MediaStream has no audio tracks (rollback glare, remote
    // mic-denied, or a track that ended immediately). The old `!stream`
    // check only covered null — not a stream with 0 tracks. On top of
    // guarding, we listen for the stream's `addtrack` event so a
    // delayed track re-arms the analyser.
    let ctx = null;
    let src = null;
    let analyser = null;
    let raf = 0;
    const start = () => {
      if (ctx) return;  // already running
      const tracks = stream.getAudioTracks();
      if (!tracks.length) return;   // wait for addtrack
      if (tracks[0].readyState !== "live") return;
      try {
        ctx = new AC();
        src = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        src.connect(analyser);
      } catch (err) {
        // Belt-and-braces — some browsers still throw despite the guard
        // (e.g. AudioContext denied by autoplay policy). Log for support
        // triage but don't crash the component.
        console.warn("[rtc] speaking analyser init failed:", err?.name, err?.message);
        ctx = null;
        return;
      }
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = buf[i] - 128; sum += v * v; }
        setSpeaking(Math.sqrt(sum / buf.length) > 6);
        raf = requestAnimationFrame(tick);
      };
      tick();
    };
    start();
    // If tracks arrive later (delayed ontrack, renegotiation), re-arm.
    const onAddTrack = () => start();
    try { stream.addEventListener("addtrack", onAddTrack); } catch (_) { /* noop */ }
    return () => {
      try { stream.removeEventListener("addtrack", onAddTrack); } catch (_) { /* noop */ }
      cancelAnimationFrame(raf);
      try { src && src.disconnect(); } catch (_) { /* noop */ }
      try { ctx && ctx.close(); }    catch (_) { /* noop */ }
    };
  }, [stream]);
  return speaking;
}

function RemoteAudio({ stream }) {  // kept for backward-compat with any old imports
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.play().catch(() => {});
    }
  }, [stream]);
  // Rendered nowhere by default; the persistent sink in RTCProvider owns audio.
  return <audio ref={ref} autoPlay playsInline style={{ display: "none" }} />;
}

/* ============================================================
 *  Participant card — HBF light aesthetic
 * ============================================================ */
function ParticipantCard({ user, connected, isSelf, stream, muted }) {
  const speaking = useSpeakingAnalyser(stream);
  const initials = (user?.name || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className={`relative flex flex-col items-center justify-center rounded-2xl p-6 min-h-[220px] bg-white transition-all
        border ${speaking
          ? "border-[var(--hx-blue)] shadow-[0_0_0_4px_var(--hx-blue-soft),0_10px_30px_-15px_rgba(37,99,235,0.35)]"
          : "border-[var(--hx-line)] shadow-[0_2px_10px_-6px_rgba(11,21,38,0.08)]"}`}
      data-testid={`participant-card-${user.id}`}
    >
      <div className="relative">
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt={user.name}
            className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md"
          />
        ) : (
          <div className="w-24 h-24 rounded-full border-2 border-white shadow-md bg-[var(--hx-blue)] flex items-center justify-center text-white text-[28px] font-extrabold">
            {initials}
          </div>
        )}
        {!connected && !isSelf && (
          <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
            <ArrowsClockwise size={20} className="text-[var(--hx-blue)] animate-spin" />
          </div>
        )}
        {muted && (
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#EF4444] border-2 border-white flex items-center justify-center">
            <MicrophoneSlash size={13} weight="fill" color="#fff" />
          </div>
        )}
      </div>
      <div className="mt-3.5 text-[var(--hx-ink)] text-[15px] font-bold">
        {user.name}{isSelf ? " (you)" : ""}
      </div>
      {(user.department || user.title) && (
        <div className="mt-0.5 text-[12px] text-[var(--hx-ink-3)]">
          {user.title || user.department}
        </div>
      )}
      <div className="mt-2 text-[11px] font-semibold flex items-center gap-1.5">
        {speaking ? (
          <span className="text-[var(--hx-blue)] flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--hx-blue)] animate-pulse" /> Speaking
          </span>
        ) : connected || isSelf ? (
          <span className="text-[var(--hx-ink-3)]">Connected</span>
        ) : (
          <span className="text-[#F59E0B]">Joining…</span>
        )}
      </div>
    </div>
  );
}

function StatusChip({ roomState, ringing }) {
  if (ringing) return <Chip color="amber" icon={<span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />} label="Ringing" />;
  const m = {
    joining:      { color: "amber",   icon: <ArrowsClockwise size={12} className="animate-spin" />, label: "Connecting" },
    connected:    { color: "green",   icon: <CheckCircle    size={12} weight="fill" />,             label: "Connected" },
    reconnecting: { color: "red",     icon: <WifiSlash      size={12} />,                            label: "Reconnecting" },
    ended:        { color: "gray",    icon: <PhoneDisconnect size={12} weight="fill" />,             label: "Call ended" },
    idle:         { color: "gray",    icon: null,                                                    label: "Idle" },
  }[roomState] || { color: "gray", label: "Idle" };
  return <Chip {...m} />;
}
function Chip({ color, icon, label }) {
  const map = {
    amber: "bg-[#FEF3C7] text-[#B45309]",
    green: "bg-[#DCFCE7] text-[#166534]",
    red:   "bg-[#FEE2E2] text-[#B91C1C]",
    gray:  "bg-[var(--hx-line-soft)] text-[var(--hx-ink-2)]",
  }[color];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${map}`}>{icon}{label}</span>;
}

/* ============================================================
 *  End-call summary — HBF light card
 * ============================================================ */
function EndedSummaryCard({ summary }) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center px-4"
      data-testid="call-ended-summary"
    >
      <div className="bg-white border border-[var(--hx-line)] rounded-2xl p-8 max-w-md w-full text-center shadow-[0_30px_80px_-20px_rgba(11,21,38,0.4)]">
        <div className="mx-auto w-14 h-14 rounded-full bg-[#FEE2E2] flex items-center justify-center mb-4">
          <PhoneDisconnect size={24} weight="fill" className="text-[#DC2626]" />
        </div>
        <div className="text-[11px] font-bold tracking-[.16em] text-[var(--hx-ink-3)] uppercase mb-1.5">Call ended</div>
        <div className="text-[var(--hx-ink)] text-[20px] font-extrabold mb-1" data-testid="ended-title">{summary.title}</div>
        <div className="text-[var(--hx-ink-2)] text-[13px] mb-5 font-mono" data-testid="ended-duration">
          Duration · {formatRtcDuration(summary.duration || 0)}
        </div>
        {summary.endedBy && (
          <div className="text-[12px] text-[var(--hx-ink-3)] mb-4" data-testid="ended-by">
            Ended by <span className="font-semibold text-[var(--hx-ink-2)]">{summary.endedBy}</span>
          </div>
        )}
        {summary.participants?.length > 0 && (
          <>
            <div className="text-[11px] font-bold tracking-[.14em] text-[var(--hx-ink-3)] uppercase mb-2">Participants</div>
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {summary.participants.map((p) => (
                <span key={p.id} className="px-2.5 py-1 rounded-full bg-[var(--hx-line-soft)] text-[var(--hx-ink)] text-[11.5px] font-semibold">
                  {p.name}
                </span>
              ))}
            </div>
          </>
        )}
        <div className="mt-6 text-[11px] font-semibold text-[var(--hx-ink-3)]">Returning to your conversation…</div>
      </div>
    </div>
  );
}

/* ============================================================
 *  Expanded call window — only rendered when rtc.expanded === true
 * ============================================================ */
export default function MeetingRoomModal() {
  const {
    activeRoom, peers, muted, onHold, expanded, ringing, roomState,
    duration, endedSummary,
    toggleMuted, toggleHold, leaveRoom, minimizeCall, cancelOutgoingRing,
  } = useRTC();
  const { user } = useAuth();

  // Ended summary can appear after activeRoom clears — always render it first.
  if (!activeRoom && endedSummary) return <EndedSummaryCard summary={endedSummary} />;
  if (!activeRoom || !expanded) return null;

  const peerList = Object.values(peers);
  const total    = peerList.length + 1;
  const cols     = Math.min(Math.max(total, 1), total >= 5 ? 3 : total);

  return (
    <div
      className="fixed inset-0 z-[60] bg-[var(--hx-line-soft)] flex flex-col"
      data-testid="meeting-room-modal"
    >
      {/* Header — HBF card top */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--hx-line)] bg-white">
        <div>
          <div className="text-[var(--hx-ink)] font-extrabold text-[18px] tracking-tight" data-testid="meeting-room-title">
            {activeRoom.title || "Internal call"}
          </div>
          <div className="mt-1 flex items-center gap-2 text-[12px] text-[var(--hx-ink-3)] font-mono">
            <span data-testid="meeting-room-duration">{formatRtcDuration(duration)}</span>
            <span className="text-[var(--hx-line)]">·</span>
            <span className="flex items-center gap-1 text-[var(--hx-ink-2)]">
              <Users size={11} />
              <span data-testid="meeting-room-participant-count">{total} in call</span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <StatusChip roomState={roomState} ringing={ringing} />
          <button
            onClick={minimizeCall}
            className="h-9 w-9 rounded-lg bg-[var(--hx-line-soft)] hover:bg-[var(--hx-line)] text-[var(--hx-ink-2)] flex items-center justify-center transition-colors"
            title="Minimize to dock"
            data-testid="meeting-room-minimize"
          >
            <ArrowsIn size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Participant grid */}
      <div className="flex-1 overflow-auto p-6">
        <div
          className="grid gap-4 mx-auto"
          style={{
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            maxWidth: total <= 2 ? "820px" : "1240px",
          }}
        >
          <ParticipantCard user={user} connected isSelf muted={muted} />
          {peerList.map((p) => (
            <ParticipantCard
              key={p.user.id}
              user={p.user}
              connected={p.connected}
              stream={p.stream}
            />
          ))}
        </div>

        {peerList.length === 0 && (
          <div className="mt-10 text-center max-w-md mx-auto">
            <div className="mx-auto w-14 h-14 rounded-full bg-white border border-[var(--hx-line)] flex items-center justify-center mb-3">
              {ringing
                ? <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-ping" />
                : <ArrowsClockwise size={20} className="text-[var(--hx-blue)] animate-spin" />}
            </div>
            <div className="text-[var(--hx-ink)] text-[15px] font-bold mb-1" data-testid="meeting-empty-heading">
              {ringing ? "Ringing others…" : "Waiting for others to join…"}
            </div>
            <div className="text-[var(--hx-ink-3)] text-[12.5px]">
              {ringing ? "They'll appear here the moment they answer." : "Share this call with your team to bring them in."}
            </div>
          </div>
        )}
      </div>

      {/* Remote audio is played by the RTCProvider's persistent sink so
          that minimizing (unmounting this modal) never stops playback. */}

      {/* Controls */}
      <div className="border-t border-[var(--hx-line)] px-6 py-4 flex items-center justify-center gap-3 bg-white">
        <ControlBtn
          onClick={() => toggleMuted()}
          active={muted}
          danger={muted}
          label={muted ? "Unmute" : "Mute"}
          testid="meeting-mute-btn"
        >
          {muted ? <MicrophoneSlash size={20} weight="fill" /> : <Microphone size={20} weight="fill" />}
        </ControlBtn>
        <ControlBtn
          onClick={toggleHold}
          active={onHold}
          label={onHold ? "Resume" : "Hold"}
          testid="meeting-hold-btn"
        >
          {onHold ? <PlayCircle size={20} weight="fill" /> : <PauseCircle size={20} weight="fill" />}
        </ControlBtn>
        {ringing && (
          <button
            onClick={cancelOutgoingRing}
            className="h-12 px-4 rounded-full bg-[var(--hx-line-soft)] hover:bg-[var(--hx-line)] text-[var(--hx-ink-2)] text-[13px] font-bold flex items-center gap-1.5"
            data-testid="meeting-cancel-ring-btn"
          >
            <PhoneDisconnect size={14} weight="fill" /> Cancel ring
          </button>
        )}
        <button
          onClick={leaveRoom}
          className="h-14 px-6 rounded-full bg-[#EF4444] hover:bg-[#DC2626] text-white text-[13px] font-bold flex items-center gap-2 shadow-[0_10px_25px_-10px_rgba(239,68,68,0.6)]"
          data-testid="meeting-leave-btn"
        >
          <PhoneDisconnect size={18} weight="fill" /> End call
        </button>
      </div>
    </div>
  );
}

function ControlBtn({ children, onClick, active, danger, label, testid }) {
  const cls = danger
    ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
    : active
      ? "bg-[var(--hx-blue-soft)] text-[var(--hx-blue)]"
      : "bg-[var(--hx-line-soft)] text-[var(--hx-ink)] hover:bg-[var(--hx-line)]";
  return (
    <button
      onClick={onClick}
      title={label}
      className={`h-14 w-14 rounded-full flex items-center justify-center transition-colors ${cls}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
